/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import useSWR from "swr";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { Grid, Link, Typography } from "@material-ui/core";
import apiRequest, { fetcher } from "utils/apiRequestWithErrorCode";
import Title from "./Title";
import StorageExecutedEvent from "./StorageExecutedEvent";
import EventMessage from "./EventMessage";
import FailDetails from "./FailDetails";
import MailDelivered from "./MailDelivered";
import MailDeliveredData from "./MailDeliveredData";
import SmimeDetails from "./SmimeDetails";
import MailSplitDetails from "./MailSplitDetails";
import MailReject from "./MailReject";
import MailSmtpDelivered from "./MailSmtpDelivered";
import { PropertyData, PropertyLabel } from "./styled";

import {
  EmailFlowLogEvent,
  EmailFlowLogDetails,
  Rule,
  MxheroShare,
  MailSplitData,
  SmimeExecutedData,
} from "@dashboard-v3/api";

export type Data = {
  save?: {
    events: any;
    files?: any[];
    storageUser: string;
    storageType: string;
  };
};

interface Props {
  event: EmailFlowLogDetails;
  recipient: string;
  includeAll?: boolean;
  showSplitRecipientStatus?: boolean;
  smtpDeliveredEvent?: EmailFlowLogDetails;
}

export default function DetailItem(props: Props) {
  const { t } = useTranslation(["tracing", "rules"]);
  const {
    event,
    recipient,
    includeAll,
    showSplitRecipientStatus,
    smtpDeliveredEvent,
  } = props;
  const [storageShares, setStorageShares] = useState<MxheroShare[]>([]);
  const { action, data, eventId } = event;
  const itemInfo = [];

  const { data: rule } = useSWR<Rule>(
    event.data?.ruleId ? `/rules/${event.data?.ruleId}` : null,
    fetcher,
    { revalidateOnFocus: false }
  );

  useEffect(() => {
    const { save } = data as Data;
    const shouldFetchShares =
      action === "storage_executed" &&
      save.storageType === "MXHERO_STORAGE" &&
      !storageShares.length;

    async function fetchStorageShareById() {
      const ids = save?.files
        .filter(f => !!f?.share?.viewUrl)
        .map(f => {
          const parts = f.share.viewUrl.split("/");
          return parts[parts.length - 1];
        });

      const res = await Promise.allSettled(
        ids.map(id => {
          return apiRequest<MxheroShare>("GET", `/mxhero-shares/${id}`);
        })
      );
      const shares = res
        .filter((r): r is PromiseFulfilledResult<MxheroShare> => {
          return r.status === "fulfilled";
        })
        .map(({ value }) => value);

      setStorageShares(shares);
    }

    if (shouldFetchShares) fetchStorageShareById();
  }, [data]);

  switch (action) {
    case "mail_received":
      if (includeAll) {
        itemInfo.push(
          <Grid item xs={12} key={eventId}>
            <PropertyLabel>{t("subject")}:</PropertyLabel>
            <PropertyData color="textPrimary">
              {data.subject as string}
            </PropertyData>
            <Grid container spacing={2} style={{ marginTop: "12px" }}>
              <MailDeliveredData data={data as EmailFlowLogEvent["data"]} />
            </Grid>
          </Grid>
        );
      }
      break;

    case "mail_delivered":
      return (
        <MailDelivered
          event={event}
          recipient={recipient}
          smtpDeliveredEvent={smtpDeliveredEvent}
        />
      );

    case "mail_smtp_delivered":
      return <MailSmtpDelivered event={event} />;

    case "mail_reject":
      return <MailReject event={event} />;

    case "rule_executed":
      itemInfo.push(
        <React.Fragment key={eventId}>
          <Grid item xs={3}>
            <PropertyLabel>{t("action")}</PropertyLabel>
            <PropertyData color="textPrimary">
              {rule && (
                <Link href={`/rules/${data.ruleId}/edit`} underline="always">
                  {t(`rules:list.actionTypes.${data.actionType}`)}
                </Link>
              )}
              {!rule && t(`rules:list.actionTypes.${data.actionType}`)}
            </PropertyData>
          </Grid>
          {rule && (
            <Grid item xs={3}>
              <PropertyLabel>{t("name")}</PropertyLabel>
              <PropertyData color="textPrimary">
                <Link href={`/rules/${rule.id}/edit`} underline="always">
                  {rule.name}
                </Link>
              </PropertyData>
            </Grid>
          )}
        </React.Fragment>
      );
      break;

    case "storage_executed":
      itemInfo.push(
        <StorageExecutedEvent
          key={eventId}
          data={data}
          rule={rule}
          shares={storageShares}
        />
      );
      break;

    case "execution_failed":
    case "rule_execution_failed":
    case "execution_unexpected_error": {
      itemInfo.push(
        <React.Fragment key={eventId}>
          <Grid item xs={rule ? 3 : 12} style={{ marginBottom: "15px" }}>
            <PropertyLabel>{t("action")}</PropertyLabel>
            <PropertyData color="textPrimary">
              {rule && (
                <Link href={`/rules/${data.ruleId}/edit`} underline="always">
                  {t(`rules:list.actionTypes.${data.actionType}`)}
                </Link>
              )}
              {!rule && t(`rules:list.actionTypes.${data.actionType}`)}
            </PropertyData>
          </Grid>
          {rule && (
            <Grid item xs={3} style={{ marginBottom: "15px" }}>
              <PropertyLabel>{t("name")}</PropertyLabel>
              <PropertyData color="textPrimary">
                <Link href={`/rules/${rule.id}/edit`} underline="always">
                  {rule.name}
                </Link>
              </PropertyData>
            </Grid>
          )}
          <Grid item xs={12}>
            <EventMessage message={data.executionMessage as string} />
            <FailDetails details={data.executionDetails} />
          </Grid>
        </React.Fragment>
      );
      break;
    }

    case "eval_filter_failed":
      itemInfo.push(
        <React.Fragment key={eventId}>
          <Grid item xs={rule ? 3 : 12} style={{ marginBottom: "15px" }}>
            <PropertyLabel>{t("action")}</PropertyLabel>
            <PropertyData color="textPrimary">
              {rule && (
                <Link href={`/rules/${data.ruleId}/edit`} underline="always">
                  {t(`rules:list.actionTypes.${data.actionType}`)}
                </Link>
              )}
              {!rule && t(`rules:list.actionTypes.${data.actionType}`)}
            </PropertyData>
          </Grid>
          {rule && (
            <Grid xs={3} style={{ marginBottom: "15px" }}>
              <PropertyLabel>{t("name")}</PropertyLabel>
              <PropertyData color="textPrimary">
                <Link href={`/rules/${rule.id}/edit`} underline="always">
                  {rule.name}
                </Link>
              </PropertyData>
            </Grid>
          )}
          <Grid item xs={12}>
            <PropertyData color="textPrimary">
              {availableMessages.includes(data.evalMessage as string)
                ? t(`filter_failed_msg.${data.evalMessage as string}`)
                : (data.evalMessage as string)}
            </PropertyData>
            <FailDetails details={data.evalDetails} />
          </Grid>
        </React.Fragment>
      );
      break;

    case "mail_split":
      if (!showSplitRecipientStatus) return null;
      itemInfo.push(
        <MailSplitDetails key={eventId} data={data as MailSplitData} />
      );
      break;

    case "mail_dropped":
      // reason === "canceled" -> Canceled by sender
      // reason === "rejected" -> Rejected by an approver
      itemInfo.push(
        data.reason ? (
          <Grid item lg={3} key={eventId}>
            <PropertyLabel>{t("reason")}</PropertyLabel>
            <PropertyData color="textPrimary">
              {translateReason(data.reason as string, t)}
            </PropertyData>
          </Grid>
        ) : (
          <></>
        )
      );
      break;

    case "smime_executed":
      const { actions } = data as SmimeExecutedData;
      itemInfo.push(<SmimeDetails key={eventId} actions={actions} />);
      break;

    case "quarantine_added":
    case "quarantine_released":
      itemInfo.push(
        <React.Fragment key={eventId}>
          <Grid item xs={3}>
            <PropertyLabel>{t("action")}</PropertyLabel>
            <PropertyData color="textPrimary">
              {rule && (
                <Link href={`/rules/${data.ruleId}/edit`} underline="always">
                  {t(`rules:list.actionTypes.${data.actionType}`)}
                </Link>
              )}
              {!rule && t(`rules:list.actionTypes.${data.actionType}`)}
            </PropertyData>
          </Grid>
          {rule && (
            <Grid item xs={3}>
              <PropertyLabel>{t("name")}</PropertyLabel>
              <PropertyData color="textPrimary">
                <Link href={`/rules/${rule.id}/edit`} underline="always">
                  {rule.name}
                </Link>
              </PropertyData>
            </Grid>
          )}
        </React.Fragment>
      );
      break;

    default:
      // Don't delete this, see here: https://mxhero.atlassian.net/browse/MXH-3655?focusedCommentId=38645
      return null;
  }

  return (
    <>
      <Title timestamp={event.createdAt}>
        <Typography style={{ fontWeight: "500" }}>
          {t(`eventActionType.${event.action}`)}
        </Typography>
      </Title>

      {itemInfo}
    </>
  );
}

function translateReason(reason: string, t: TFunction) {
  if (reason === "canceled") return t("droppedReason.canceled");
  if (reason === "rejected") return t("droppedReason.rejected");
  return reason;
}

const availableMessages = [
  "mail_content_without_attachments",
  "sender_filtered_by_rule",
  "rule_shortcode_avoid_processing",
  "rule_shortcode_sender_not_managed_by_rule",
  "mail_content_is_tnef",
  "mail_content_failed_to_check_filter",
  "flow_filter_from_to",
  "eval_email_flow_managed_failed",
];
